import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { ProjectTemplateRecipientType } from '../../config/templateConfig'
import { projectService } from '../../services/projectService'
import { statusReportService } from '../../services/statusReportService'
import type { Project, ProjectStatus } from '../../types/project'
import { formatFullDateTime } from '../../utils/dateUtils'
import Modal from '../Modal'
import StatusBadge from '../StatusBadge'
import StatusReportConfirmationModal from '../report/StatusReportConfirmationModal'
import ProjectConfirmation from './ProjectConfirmation'

interface ViewProjectModalProps {
  project: Project
  onClose: () => void
}

const ViewProjectModal: React.FC<ViewProjectModalProps> = ({
  project,
  onClose,
}) => {
  const navigate = useNavigate()
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportTags, setReportTags] = useState<
    { id: number; label?: string; status?: string; title?: string | null }[]
  >([])
  const [isLoadingTags, setIsLoadingTags] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState(false)

  const handleGenerateReport = async () => {
    setIsLoadingTags(true)
    try {
      const { tag_ids, count } =
        await statusReportService.getProjectCompletedTagIds(project.id)
      if (count === 0) {
        toast.warning('No completed tags found for this project')
        return
      }
      setReportTags(tag_ids.map((id) => ({ id, status: 'COMPLETE' })))
      setShowReportModal(true)
    } catch (err) {
      console.error('Failed to fetch completed tags:', err)
      toast.error('Failed to fetch completed tags')
    } finally {
      setIsLoadingTags(false)
    }
  }

  const handleConfirmReport = async (
    recipientUserIds: number[],
    notes: string,
  ) => {
    setIsGeneratingReport(true)
    try {
      const tagIds = reportTags.map((t) => t.id)
      const { statusReportId } = await statusReportService.generateReport({
        tag_ids: tagIds,
        recipient_user_ids:
          recipientUserIds.length > 0 ? recipientUserIds : null,
        notes: notes || null,
      })
      setShowReportModal(false)
      onClose()
      navigate(`/tag-report/${statusReportId}`)
    } catch (err) {
      console.error('Failed to generate report:', err)
      toast.error('Failed to generate report')
    } finally {
      setIsGeneratingReport(false)
    }
  }

  const handleDownloadPdf = async () => {
    try {
      const blob = await projectService.downloadProjectPdf(project.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `project_terms_${project.label}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download PDF:', error)
    }
  }

  const templateRecipientType = project.template_recipient_type as
    | ProjectTemplateRecipientType
    | null
    | undefined

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <div className={css(styles.subHeaderRow)}>
            {project.label && (
              <span className={css(styles.projectLabel)}>
                ID: {project.label}
              </span>
            )}
            {project.status === 'ACCEPTED' && project.accepted_at && (
              <span className={css(styles.dateLabel)}>
                Accepted: {formatFullDateTime(project.accepted_at)}
              </span>
            )}
            {(project.status === 'DECLINED' || project.status === 'PENDING') &&
              project.sent_at_formatted && (
                <span className={css(styles.dateLabel)}>
                  Sent: {project.sent_at_formatted}
                </span>
              )}
          </div>
          <div className={css(styles.titleRow)}>
            <h2 className={css(styles.title)}>
              {project.status === 'ACCEPTED'
                ? 'Project Terms – Details'
                : project.status === 'DECLINED'
                  ? 'Project Terms – Declined'
                  : 'Project Terms – Pending'}
            </h2>
            <StatusBadge status={project.status as ProjectStatus} />
          </div>
        </div>
        <div className={css(styles.content)}>
          {/* Decline Information Section - shown at top for declined projects */}
          {project.status === 'DECLINED' && (
            <>
              <div className={css(styles.declineSection)}>
                {project.declined_at && (
                  <div className={css(styles.declineDateRow)}>
                    <span className={css(styles.declineDateLabel)}>
                      Declined Date:
                    </span>
                    <span className={css(styles.declineDateValue)}>
                      {formatFullDateTime(project.declined_at)}
                    </span>
                  </div>
                )}
                {project.decline_reason && (
                  <>
                    <h4 className={css(styles.declineTitle)}>
                      Decline Reason:
                    </h4>
                    <p className={css(styles.declineReason)}>
                      {project.decline_reason}
                    </p>
                  </>
                )}
                {(project.declined_by || project.declined_by_first_name) && (
                  <p className={css(styles.declinedBy)}>
                    Declined by{' '}
                    {project.declined_by_first_name
                      ? `${project.declined_by_first_name} ${project.declined_by_last_name || ''}`.trim() ||
                        project.declined_by_email
                      : `${project.declined_by?.first_name || ''} ${project.declined_by?.last_name || ''}`.trim() ||
                        project.declined_by?.email}
                  </p>
                )}
              </div>
              <hr className={css(styles.divider)} />
            </>
          )}

          {/* Reuse ProjectConfirmation for consistent field display */}
          <ProjectConfirmation
            data={project}
            contacts={[]}
            useDirectData={true}
            templateRecipientType={templateRecipientType}
            showAcceptedDate={project.status === 'ACCEPTED'}
          />
        </div>
        <div className={css(styles.footer)}>
          {project.status === 'ACCEPTED' && (
            <>
              <button
                onClick={handleGenerateReport}
                className={css(styles.reportBtn)}
                disabled={isLoadingTags}
              >
                {isLoadingTags ? (
                  <i className="fa-solid fa-spinner fa-spin" />
                ) : (
                  <i className="fa-solid fa-chart-line" />
                )}{' '}
                Generate Report
              </button>
              <button
                onClick={handleDownloadPdf}
                className={css(styles.downloadBtn)}
              >
                <i className="fa-solid fa-download" /> Download PDF
              </button>
            </>
          )}
          <button onClick={onClose} className={css(styles.closeBtn)}>
            Close
          </button>
        </div>
      </div>
      {showReportModal && (
        <StatusReportConfirmationModal
          isOpen={showReportModal}
          completedTags={reportTags}
          onClose={() => setShowReportModal(false)}
          onConfirm={handleConfirmReport}
          isSubmitting={isGeneratingReport}
        />
      )}
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  projectLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  dateLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #E5E7EB',
    margin: '24px 0',
  },
  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#4B5563',
    },
  },
  reportBtn: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    marginRight: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#1D4ED8',
    },
  },
  downloadBtn: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    marginRight: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  declineSection: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    padding: '16px',
  },
  declineTitle: {
    margin: 0,
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#92400E',
  },
  declineReason: {
    margin: 0,
    marginBottom: '12px',
    fontSize: '14px',
    color: '#78350F',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  declinedBy: {
    margin: 0,
    fontSize: '12px',
    color: '#B45309',
    fontStyle: 'italic',
  },
  declineDateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  declineDateLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#92400E',
  },
  declineDateValue: {
    fontSize: '14px',
    color: '#78350F',
  },
})

export default ViewProjectModal
